const EMAIL_REGEX = /^\S+@\S+\.\S+$/;
const MAX_TOTAL_UPLOAD_BYTES = 3 * 1024 * 1024;

const getApplicantDetails = (values = {}) => ({
  name: String(values.fullName || values.ownerName || values.businessName || '').trim(),
  email: String(values.email || '').trim(),
  phone: String(values.phone || '').trim(),
});

const matchesShowWhenRule = (showWhen = {}, values = {}) =>
  Object.entries(showWhen).every(([fieldName, expected]) => {
    const currentValue = values[fieldName];

    if (Array.isArray(expected)) {
      return expected.includes(currentValue);
    }

    return currentValue === expected;
  });

export const isFieldVisible = (field, values = {}) => {
  if (!field.showWhen) {
    return true;
  }

  return matchesShowWhenRule(field.showWhen, values);
};

const matchesAcceptRule = (file, acceptValue) => {
  if (!acceptValue) return true;

  const rules = acceptValue
    .split(',')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);

  if (rules.length === 0) return true;

  const fileName = (file.name || '').toLowerCase();
  const mimeType = (file.type || '').toLowerCase();

  return rules.some((rule) => {
    if (rule.startsWith('.')) {
      return fileName.endsWith(rule);
    }

    if (rule.endsWith('/*')) {
      return mimeType.startsWith(rule.slice(0, -1));
    }

    return mimeType === rule;
  });
};

export const buildInitialValues = (fields) =>
  fields.reduce((acc, field) => {
    acc[field.name] = field.type === 'file' ? [] : '';
    return acc;
  }, {});

export const validateServiceField = (field, value, values = {}) => {
  if (!isFieldVisible(field, values)) {
    return undefined;
  }

  if (field.type === 'file') {
    const files = Array.isArray(value) ? value : [];

    if (field.required && files.length === 0) {
      return `${field.label} is required.`;
    }

    if (field.maxFiles && files.length > field.maxFiles) {
      return `Upload at most ${field.maxFiles} file${field.maxFiles > 1 ? 's' : ''} for ${field.label}.`;
    }

    for (const file of files) {
      if (!matchesAcceptRule(file, field.accept)) {
        return `${field.label} must be a PDF, JPG, or PNG file.`;
      }

      if (field.maxFileSizeMB && file.size > field.maxFileSizeMB * 1024 * 1024) {
        return `${field.label} must be smaller than ${field.maxFileSizeMB} MB.`;
      }
    }

    return undefined;
  }

  const normalizedValue = typeof value === 'string' ? value.trim() : value;

  if (field.required && !normalizedValue) {
    return `${field.label} is required.`;
  }

  if (!normalizedValue) {
    return undefined;
  }

  if (field.type === 'email' && !EMAIL_REGEX.test(normalizedValue)) {
    return 'Enter a valid email address.';
  }

  if (field.minLength && normalizedValue.length < field.minLength) {
    return `${field.label} must be at least ${field.minLength} characters.`;
  }

  if (field.maxLength && normalizedValue.length > field.maxLength) {
    return `${field.label} must be at most ${field.maxLength} characters.`;
  }

  if (field.pattern) {
    const pattern = new RegExp(field.pattern);
    if (!pattern.test(normalizedValue)) {
      return field.patternMessage || `Enter a valid ${field.label.toLowerCase()}.`;
    }
  }

  return undefined;
};

export const validateServiceForm = (fields, values) =>
  fields.reduce((acc, field) => {
    const error = validateServiceField(field, values[field.name], values);
    if (error) {
      acc[field.name] = error;
    }
    return acc;
  }, {});

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
    reader.readAsDataURL(file);
  });

const serializeFile = async (file, label) => {
  const dataUrl = await readFileAsDataUrl(file);
  const [, contentBase64 = ''] = String(dataUrl).split(',');

  return {
    fieldLabel: label,
    filename: file.name,
    contentType: file.type || 'application/octet-stream',
    size: file.size,
    contentBase64,
  };
};

const buildServiceSubmissionPayload = async (service, fields, values, payment) => {
  const detailEntries = [];
  const attachmentEntries = [];
  let totalUploadBytes = 0;

  for (const field of fields) {
    if (!isFieldVisible(field, values)) {
      continue;
    }

    const value = values[field.name];

    if (field.type === 'file') {
      const files = Array.isArray(value) ? value : [];

      for (const file of files) {
        totalUploadBytes += file.size;
        if (totalUploadBytes > MAX_TOTAL_UPLOAD_BYTES) {
          throw new Error('Please keep the total upload size under 3 MB for email delivery.');
        }

        attachmentEntries.push(await serializeFile(file, field.label));
      }

      continue;
    }

    detailEntries.push({
      name: field.name,
      label: field.label,
      value: String(value).trim(),
    });
  }

  const applicant = getApplicantDetails(values);

  return {
    submissionType: 'service',
    serviceId: service.id,
    serviceSlug: service.slug,
    serviceTitle: service.title,
    name: applicant.name,
    email: applicant.email,
    phone: applicant.phone,
    details: detailEntries,
    attachments: attachmentEntries,
    payment,
  };
};

const postJson = async (url, payload) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  const responseBody = await response.json().catch(() => ({}));

  if (!response.ok) {
    if (response.status === 413) {
      throw new Error('Uploaded documents are too large. Please keep the combined file size under 3 MB.');
    }

    throw new Error(responseBody.error || 'Request failed.');
  }

  return responseBody;
};

const postEmailPayload = async (payload) => {
  return postJson('/api/send-email', payload);
};

export const createServicePaymentOrder = async (service, values) => {
  const applicant = getApplicantDetails(values);

  return postJson('/api/create-payment-order', {
    serviceId: service.id,
    serviceTitle: service.title,
    name: applicant.name,
    email: applicant.email,
    phone: applicant.phone,
  });
};

export const verifyServicePayment = async ({ orderId, paymentId, signature }) => {
  return postJson('/api/verify-payment', {
    orderId,
    paymentId,
    signature,
  });
};

export const submitServiceRequest = async (service, fields, values, payment) => {
  const payload = await buildServiceSubmissionPayload(service, fields, values, payment);
  return postEmailPayload(payload);
};

export const submitContactRequest = async (form) =>
  postEmailPayload({
    submissionType: 'contact',
    ...form,
  });
